import { NextRouter } from "next/router"
import { showDialogInfo } from "@/hooks/use-dialog-info"
import { InspirationRef } from "@/types/entity"
import {
  AbstractInspiration,
  DefaultInspirationPerPage,
  DetailedInspiration,
  DetailedInspirationItem,
  InitGuideProps,
  Inspiration,
  InspirationItem,
  InspirationItemBlockBodyText,
  InspirationItemBlockDaySection,
  InspirationItemBlockHeaderLarge,
  InspirationItemBlockHeaderMedium,
  InspirationItemBlockHeaderNormal,
  InspirationSummary,
} from "@/types/inspiration"
import { AxiosInstance } from "axios"
import cloneDeep from "lodash/cloneDeep"

import { InspirationApi } from "@/lib/inspiration-api"
import { links } from "@/lib/routes"
import { assert } from "@/lib/utils/assertions"
import { blank, present } from "@/lib/utils/presence"
import { GuideProps } from "@/components/guide"
import { isDetailedEntity, toPlacePlaceholder } from "./entity-utils"

export const DefaultInspirationItemFetchSize = 20
export const DefaultInspirationTableOfContentFetchSize = 500

export const isInspirationHasDaySection = (inspiration: Inspiration) => {
  return inspiration.total_days > 0
}

export const isInspirationJustList = (inspiration: Inspiration) => {
  return !isInspirationHasDaySection(inspiration)
}

export const isInspirationPartiallyLoaded = (
  inspiration: AbstractInspiration
) => {
  return !isInspirationFullyLoaded(inspiration)
}

export const isInspirationFullyLoaded = (inspiration: AbstractInspiration) => {
  if (inspiration.fetch_count && inspiration.total_count) {
    return inspiration.fetch_count >= inspiration.total_count
  } else {
    // If we don't have count, we must be fully loaded already
    return true
  }
}

export const isInspirationItemFetchedAtIdx = (
  inspiration: AbstractInspiration,
  idx: number
) => {
  if (inspiration.fetch_count && inspiration.total_count) {
    const currFetchedIdx = inspiration.fetch_count
      ? inspiration.fetch_count - 1
      : 0
    return idx <= currFetchedIdx
  } else {
    // If we don't have count, we must be fully loaded already
    return true
  }
}

export const isInspirationItemHeaderBlockLarge = (item: InspirationItem) => {
  return item.block_type === InspirationItemBlockHeaderLarge
}

export const isInspirationItemHeaderBlockMedium = (item: InspirationItem) => {
  return item.block_type === InspirationItemBlockHeaderMedium
}

export const isInspirationItemHeaderBlockNormal = (item: InspirationItem) => {
  return item.block_type === InspirationItemBlockHeaderNormal
}

/** Headings and body text: the blocks that split on Enter and merge on Backspace */
export const isInspirationItemTextBlock = (
  item: Pick<InspirationItem, "block_type">
) => {
  return (
    isInspirationItemHeaderBlockType(item) ||
    item.block_type === InspirationItemBlockBodyText
  )
}

export const isInspirationItemDaySectionBlock = (item: InspirationItem) => {
  return item.block_type === InspirationItemBlockDaySection
}

export const isInspirationItemHeaderBlockType = (
  item: Pick<InspirationItem, "block_type">
) => {
  return (
    item.block_type === InspirationItemBlockHeaderLarge ||
    item.block_type === InspirationItemBlockHeaderMedium ||
    item.block_type === InspirationItemBlockHeaderNormal
  )
}

export const isInspirationItemDetailed = (item: InspirationItem) => {
  return item.detailed
}

export const isInspirationItemSummary = (item: InspirationItem) => {
  return !item.detailed
}

export async function fetchGuideProps(
  axios: AxiosInstance,
  entityRef: InspirationRef,
  fullFetch?: boolean
): Promise<
  Omit<GuideProps, "canAdmin" | "isEditable" | "showRegisterGate"> &
    InitGuideProps
> {
  const inspirationApi = new InspirationApi(axios)

  const guide = await inspirationApi.fetchInspiration(
    entityRef,
    fullFetch === false ? DefaultInspirationItemFetchSize : undefined
  )

  return {
    guide,
  }
}

export function inspirationTotalDayList(
  inspirationDetail: Inspiration
): number[] {
  return [...Array(inspirationDetail.total_days).keys()]
}

export function sortInspirationItemBySortOrder(
  item1: InspirationItem,
  item2: InspirationItem
): number {
  return item1.sort_order - item2.sort_order
}

export function inspirationItemsSortBySortOrder(
  inspirationDetail: DetailedInspiration
) {
  // Sort by time of Day (ignore the date part)
  const itemList = [...inspirationDetail.items]
  return itemList.sort((item1, item2) => {
    return sortInspirationItemBySortOrder(item1, item2)
  })
}

export function inspirationItemShouldShowQuote(
  dayQuoteShown: boolean[],
  dayNum: number
) {
  if (dayQuoteShown[dayNum]) {
    return false
  } else {
    dayQuoteShown[dayNum] = true
    return true
  }
}

export function inspirationItemShouldShowTimeTitle(
  inspirationItem: DetailedInspirationItem
) {
  return inspirationItem.show_title
}

export function inspirationItemShouldShowTime(
  inspirationItem: DetailedInspirationItem
) {
  return inspirationItem.show_time
}

export function inspirationItemShouldShowDayTitle(
  dayTitleShown: boolean[],
  dayNum: number
) {
  if (dayTitleShown[dayNum]) {
    return false
  } else {
    dayTitleShown[dayNum] = true
    return true
  }
}

// export function inspirationItemShouldShowTimeTitle(
//   dayTimeShown: { [key: string]: boolean }[],
//   dayNum: number,
//   time: string
// ) {
//   // For now, show the title and time and don't collapse them together
//   // as when it is collapsed, it really hard to split it out by end user.

//   const dayTimeTitleShownList = dayTimeShown[dayNum]
//   if (dayTimeTitleShownList) {
//     if (dayTimeTitleShownList[time]) {
//       return false
//     } else {
//       dayTimeTitleShownList[time] = true
//       return true
//     }
//   } else {
//     const dayTimeTitleShownList: { [key: string]: boolean } = {}
//     dayTimeShown[dayNum] = dayTimeTitleShownList
//     dayTimeTitleShownList[time] = true
//     return true
//   }
// }

export const calculateItemsPerRow = () => {
  const inspirationCards = document.getElementsByClassName(
    "@container/inspire-card"
  ) as HTMLCollectionOf<HTMLElement>
  // Don't calculate unless we have more than 15 (i.e. enough to fill up a page)
  if (present(inspirationCards)) {
    let firstRowCount = 0
    const firstRowTop = inspirationCards[0].offsetTop
    for (const inspirationCard of inspirationCards) {
      if (inspirationCard.offsetTop !== firstRowTop) {
        break
      }
      firstRowCount++
    }
    return firstRowCount
  }
  return 0
}

export const calculateItemsPerPage = (itemsPerRow: number) => {
  return itemsPerRow % 2 === 1
    ? DefaultInspirationPerPage
    : DefaultInspirationPerPage + 1
}

export const fetchInspirationItemPerPageSize = (
  inspiration: AbstractInspiration
) => {
  return inspiration ? DefaultInspirationItemFetchSize : 0
}

export type existingItemsMapType = Map<
  number,
  { item: DetailedInspirationItem; pos: number }
>

export const mergePaginatedInspirationItems = (
  newInspiration: DetailedInspiration,
  newItems: DetailedInspirationItem[],
  existingItemsMap?: Map<number, { item: InspirationItem; pos: number }>
) => {
  // If for some reasons, our index is off, we might fetch duplicdate items.
  // Try to not add duplicate items:

  // Create a map for faster look up
  existingItemsMap =
    existingItemsMap ||
    new Map(newInspiration.items.map((item, pos) => [item.id, { item, pos }]))
  if (blank(existingItemsMap)) {
    const oldItemsMap = new Map(
      newInspiration.items.map((item, pos) => [item.id, { item, pos }])
    )
    oldItemsMap.forEach((value, key) => {
      existingItemsMap.set(key, value) // Adds or updates the key-value pair
    })
  }

  assert(existingItemsMap, "existingItemsMap should exists")

  newInspiration = cloneDeep(newInspiration)

  // Now merge in duplicate if any or just append
  newItems.map((newItem) => {
    const found = existingItemsMap.get(newItem.id)
    if (found) {
      newInspiration.items[found.pos] = newItem
    } else {
      newInspiration.items.push(newItem)
      existingItemsMap.set(newItem.id, {
        item: newItem,
        pos: newInspiration.items.length - 1,
      })
    }
  })

  newInspiration.fetch_count = newInspiration.items.length
  return newInspiration
}

export const updateInspirationItem = (
  inspiration: DetailedInspiration,
  newInspirationItem: DetailedInspirationItem
) => {
  const newInspiration = cloneDeep(inspiration)

  newInspiration.items = newInspiration.items.map((inspirationItem) =>
    inspirationItem.id === newInspirationItem.id
      ? newInspirationItem
      : inspirationItem
  )
  return newInspiration
}

export function showPrivateGuideDialog(
  inspiration: Inspiration,
  router: NextRouter
) {
  return showDialogInfo({
    title: "Private guide",
    message: "Sorry, this guide is currently private and cannot be viewed.",
    cancelLabel: "View Creator’s profile",
    okLabel: "OK",
    okVariant: "primary",
    cancelAction: () =>
      void router.push(links.profile(inspiration.owner.handle, "guides")),
  })
}

export function showUnPublishedGuideDialog(
  inspiration: Inspiration,
  router: NextRouter
) {
  return showDialogInfo({
    title: "Unpublished guide",
    message: "Sorry, this guide is currently not yet published.",
    cancelLabel: "View Creator’s profile",
    okLabel: "OK",
    okVariant: "primary",
    cancelAction: () =>
      void router.push(links.profile(inspiration.owner.handle, "guides")),
  })
}

export function updateInspiration(
  inspiration: Inspiration,
  newInspiration: Inspiration
): Inspiration {
  if (isDetailedEntity(inspiration)) {
    return updateDetailedInspiration(inspiration, newInspiration)
  } else {
    return updateInspirationSummary(inspiration, newInspiration)
  }
}

export function updateInspirationSummary(
  inspirationSummary: InspirationSummary,
  newInspiration: Inspiration
): InspirationSummary {
  const convertedInspirationSummary = isDetailedEntity(newInspiration)
    ? convertInspirationToInspirationSummary(newInspiration)
    : newInspiration
  const ret = { ...inspirationSummary, ...convertedInspirationSummary }
  return cloneDeep(ret)
}

export function updateDetailedInspiration(
  detailedInspiration: DetailedInspiration,
  newInspiration: Inspiration
): DetailedInspiration {
  // Don't want the detailed field to pollute the other.
  // eslint-disable-next-line unused-imports/no-unused-vars
  const { detailed, ...filteredNewInspiration } = newInspiration
  const merged = { ...detailedInspiration, ...filteredNewInspiration }
  return cloneDeep(merged)
}

function syncCustomMapPlaces(
  inspiration: DetailedInspiration,
  items: DetailedInspirationItem[]
) {
  if (!inspiration.custom_map_places) return

  items.forEach((item) => {
    const pins = inspiration.custom_map_places
    const index = pins.findIndex(
      (place) => place.inspiration_item_id === item.id
    )

    if (item.place || item.latitude == null || item.longitude == null) {
      if (index >= 0) pins.splice(index, 1)
      return
    }

    const pin = {
      inspiration_item_id: item.id,
      name: item.title,
      address: item.address,
      latitude: item.latitude,
      longitude: item.longitude,
    }
    if (index >= 0) {
      pins[index] = pin
    } else {
      pins.push(pin)
    }
  })
}

export function insertInspirationItems(
  inspiration: DetailedInspiration,
  updatedInspirationItems: DetailedInspirationItem[]
) {
  if (blank(updateInspirationItems)) {
    return inspiration
  }

  const allInspirationItems = updatedInspirationItems

  // The inserted item is always the first item
  const insertedInspirationItem = updatedInspirationItems[0]
  updatedInspirationItems = updatedInspirationItems.slice(1)

  const newInspiration = cloneDeep(inspiration)
  const updatedInspirationItemsMap = Object.fromEntries(
    updatedInspirationItems.map((item) => [item.id, item])
  )
  newInspiration.items = newInspiration.items.map(
    (item) => updatedInspirationItemsMap[item.id] || item
  )
  newInspiration.items.unshift(insertedInspirationItem)

  syncCustomMapPlaces(newInspiration, allInspirationItems)

  newInspiration.items = inspirationItemsSortBySortOrder(newInspiration)

  newInspiration.fetch_count = newInspiration.fetch_count
    ? newInspiration.fetch_count + 1
    : 1
  newInspiration.total_count = newInspiration.total_count
    ? newInspiration.total_count + 1
    : 1

  return newInspiration
}

export function updateInspirationItems(
  inspiration: DetailedInspiration,
  updatedInspirationItems: DetailedInspirationItem[]
): DetailedInspiration {
  if (blank(updateInspirationItems)) {
    return inspiration
  }

  const newInspiration = cloneDeep(inspiration)
  const updatedInspirationItemsMap = Object.fromEntries(
    updatedInspirationItems.map((item) => [item.id, item])
  )
  newInspiration.items = newInspiration.items.map(
    (item) => updatedInspirationItemsMap[item.id] || item
  )

  updatedInspirationItems.forEach((item) => {
    if (item.place && newInspiration.map_places) {
      const placeholder = toPlacePlaceholder(item.place)
      if (!placeholder) return

      const placeExists = newInspiration.map_places.some(
        (place) => place.entity_ref === placeholder.entity_ref
      )

      if (!placeExists) {
        newInspiration.map_places.push(placeholder)
      }
    }
  })

  syncCustomMapPlaces(newInspiration, updatedInspirationItems)

  newInspiration.items = inspirationItemsSortBySortOrder(newInspiration)

  return newInspiration
}

export function removeInspirationItems(
  inspiration: DetailedInspiration,
  updatedInspirationItems: DetailedInspirationItem[]
) {
  if (blank(updateInspirationItems)) {
    return inspiration
  }

  // The deleted item is always the first item
  const deletedInspirationItem = updatedInspirationItems[0]
  updatedInspirationItems = updatedInspirationItems.slice(1)

  const newInspiration = cloneDeep(inspiration)
  const updatedInspirationItemsMap = Object.fromEntries(
    updatedInspirationItems.map((item) => [item.id, item])
  )
  newInspiration.items = newInspiration.items.map(
    (item) => updatedInspirationItemsMap[item.id] || item
  )
  newInspiration.items = newInspiration.items.filter(
    (item) => item.id !== deletedInspirationItem.id
  )

  // The same place can appear more than once; keep its pin while another
  // item still references it.
  const deletedPlaceRef = deletedInspirationItem.place?.entity_ref
  if (
    deletedPlaceRef &&
    !newInspiration.items.some(
      (item) => item.detailed && item.place?.entity_ref === deletedPlaceRef
    )
  ) {
    newInspiration.map_places = newInspiration.map_places.filter(
      (place) => place.entity_ref !== deletedPlaceRef
    )
  }

  if (newInspiration.custom_map_places) {
    newInspiration.custom_map_places = newInspiration.custom_map_places.filter(
      (place) => place.inspiration_item_id !== deletedInspirationItem.id
    )
  }

  newInspiration.items = inspirationItemsSortBySortOrder(newInspiration)

  newInspiration.fetch_count = newInspiration.fetch_count
    ? newInspiration.fetch_count - 1
    : 1
  newInspiration.total_count = newInspiration.total_count
    ? newInspiration.total_count - 1
    : 1

  return newInspiration
}

export function getInspirationUrl(inspiration: Inspiration) {
  return links.inspiration(inspiration)
}

export function convertInspirationToInspirationSummary(
  inspiration: Inspiration
): InspirationSummary {
  if (isDetailedEntity(inspiration)) {
    const { detailed: _, ...rest } = inspiration as Omit<
      DetailedInspiration,
      | "items"
      | "day_details"
      | "destination_items"
      | "map_places"
      | "custom_map_places"
    >
    return {
      ...rest,
      detailed: false,
    }
  } else {
    return inspiration
  }
}

export type InspirationMoveTarget = { beforeId: number } | { afterId: number }

// Reorders the guide locally before the server confirms a move. The moved item
// gets a provisional sort_order between its new neighbours, because every save
// path re-sorts items by sort_order; the server's value replaces it on reply.
export function moveInspirationItemLocally(
  inspiration: DetailedInspiration,
  itemId: number,
  target: InspirationMoveTarget
): DetailedInspiration {
  const targetId = "beforeId" in target ? target.beforeId : target.afterId
  const moved = inspiration.items.find((item) => item.id === itemId)
  if (!moved || targetId === itemId) return inspiration

  const rest = inspiration.items.filter((item) => item.id !== itemId)
  const targetIndex = rest.findIndex((item) => item.id === targetId)
  if (targetIndex < 0) return inspiration

  const insertIndex = "beforeId" in target ? targetIndex : targetIndex + 1
  const prev = rest[insertIndex - 1]
  const next = rest[insertIndex]
  let sortOrder: number
  if (prev && next) {
    sortOrder = (prev.sort_order + next.sort_order) / 2
  } else if (next) {
    sortOrder = next.sort_order - 1
  } else if (prev) {
    sortOrder = prev.sort_order + 1
  } else {
    sortOrder = moved.sort_order
  }

  const items = [...rest]
  items.splice(insertIndex, 0, { ...moved, sort_order: sortOrder })
  return { ...inspiration, items }
}
